import clsx from 'clsx';
import type { SignalFieldMappings } from '@arcanum-hq/shared-types';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCompactCurrency, formatPercent } from '@/lib/format';

// deal-signal-overhaul Phase 4 Session 3 (2026-05-12) + Lovable-parity rewrite
// (2026-05-12).
//
// Zone 5 of the Signal page — operating-asset only. 5-row × (In-Place /
// Stabilized / Δ CHANGE) grid mirroring Lovable's terminal-style table at
// `dashboard-arcanum/src/pages/PublicDealView.tsx:898-984`. Each Δ cell
// shows BOTH absolute change AND relative percentage stacked, with an
// accent-tinted column background.

type RowFormat = 'currency' | 'percentage';

interface ComparisonRowConfig {
  metric: string;
  beforeKey: keyof SignalFieldMappings;
  afterKey: keyof SignalFieldMappings;
  format: RowFormat;
}

const ROWS: ReadonlyArray<ComparisonRowConfig> = [
  {
    metric: 'Avg Rent',
    beforeKey: 'comparisonAvgRent_inPlace',
    afterKey: 'comparisonAvgRent_stabilized',
    format: 'currency',
  },
  {
    metric: 'NOI',
    beforeKey: 'comparisonNOI_inPlace',
    afterKey: 'comparisonNOI_stabilized',
    format: 'currency',
  },
  {
    metric: 'Occupancy',
    beforeKey: 'comparisonOccupancy_inPlace',
    afterKey: 'comparisonOccupancy_stabilized',
    format: 'percentage',
  },
  {
    metric: 'Operating Margin',
    beforeKey: 'comparisonOperatingMargin_inPlace',
    afterKey: 'comparisonOperatingMargin_stabilized',
    format: 'percentage',
  },
  {
    metric: 'Implied Value',
    beforeKey: 'comparisonImpliedValue_inPlace',
    afterKey: 'comparisonImpliedValue_stabilized',
    format: 'currency',
  },
];

function toFiniteNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatCell(value: number | null, format: RowFormat): string {
  if (value == null) return '—';
  if (format === 'currency') return formatCompactCurrency(value);
  return formatPercent(value, 1);
}

interface DeltaCellState {
  abs: string;
  pct: string;
  tone: 'positive' | 'negative' | 'neutral';
}

function computeDelta(
  before: number | null,
  after: number | null,
  format: RowFormat,
): DeltaCellState {
  if (before == null || after == null) {
    return { abs: '—', pct: '', tone: 'neutral' };
  }
  if (format === 'percentage') {
    const pointDelta = (after - before) * 100;
    if (!Number.isFinite(pointDelta) || Math.abs(pointDelta) < 0.05) {
      return { abs: '±0pp', pct: '', tone: 'neutral' };
    }
    const sign = pointDelta > 0 ? '+' : '';
    return {
      abs: `${sign}${pointDelta.toFixed(1)}pp`,
      pct: '',
      tone: pointDelta > 0 ? 'positive' : 'negative',
    };
  }
  // Currency: absolute + relative.
  const absDelta = after - before;
  const absSign = absDelta > 0 ? '+' : absDelta < 0 ? '−' : '';
  const absText = `${absSign}${formatCompactCurrency(Math.abs(absDelta))}`;
  if (before === 0) {
    return {
      abs: after > 0 ? absText : '—',
      pct: after > 0 ? '+∞' : '',
      tone: after > 0 ? 'positive' : 'neutral',
    };
  }
  const ratio = (after - before) / before;
  if (!Number.isFinite(ratio) || ratio === 0) {
    return { abs: '±0', pct: '', tone: 'neutral' };
  }
  const pctVal = ratio * 100;
  const pctSign = pctVal > 0 ? '+' : '';
  return {
    abs: absText,
    pct: `${pctSign}${pctVal.toFixed(1)}%`,
    tone: pctVal > 0 ? 'positive' : 'negative',
  };
}

const DELTA_TONE_TOKEN: Record<DeltaCellState['tone'], string> = {
  positive: 'text-accent',
  negative: 'text-warn',
  neutral: 'text-text-dim',
};

export interface BeforeAfterComparisonProps {
  context: SignalFieldResolverContext;
  className?: string;
}

// Shared row track + header alignment.
const ROW_COLS = 'grid-cols-[minmax(120px,1.5fr)_1fr_1fr_1.1fr]';

export function BeforeAfterComparison({
  context,
  className,
}: BeforeAfterComparisonProps) {
  const resolver = useSignalFieldResolver(context);

  if (!resolver.isReady) {
    return (
      <div
        data-testid="before-after-comparison"
        data-state="loading"
        className={clsx('px-2 py-2', className)}
      >
        <div className={clsx('grid gap-x-3 gap-y-2', ROW_COLS)}>
          {ROWS.map((row) => (
            <span
              key={`skeleton-${row.metric}`}
              data-testid="before-after-skeleton-row"
              className="col-span-4"
            >
              <Skeleton variant="line" />
            </span>
          ))}
        </div>
      </div>
    );
  }

  const rows = ROWS.map((cfg) => {
    const before = toFiniteNumber(resolver.resolve(cfg.beforeKey));
    const after = toFiniteNumber(resolver.resolve(cfg.afterKey));
    return {
      cfg,
      before,
      after,
      delta: computeDelta(before, after, cfg.format),
    };
  });

  return (
    <div
      data-testid="before-after-comparison"
      data-state="ready"
      className={clsx('flex flex-col', className)}
    >
      <div className="flex items-center gap-2.5 px-3 pt-2 pb-1">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold shrink-0">
          In-Place / Stabilized
        </h3>
        <span className="flex-1 h-px bg-panel-border" />
      </div>
      {/* Header band */}
      <div
        className={clsx(
          'grid items-center border-b border-panel-border',
          ROW_COLS,
        )}
        style={{ background: 'rgba(0, 200, 83, 0.024)' }}
      >
        <div className="px-3 py-1.5 text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          Metric
        </div>
        <div className="px-3 py-1.5 text-right text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          In-Place
        </div>
        <div className="px-3 py-1.5 text-right text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          Stabilized
        </div>
        <div
          className="px-3 py-1.5 text-right text-[8.5px] font-mono text-accent uppercase tracking-[1px] font-semibold"
          style={{ background: 'rgba(0, 200, 83, 0.05)' }}
        >
          Δ Change
        </div>
      </div>
      {/* Rows */}
      {rows.map(({ cfg, before, after, delta }) => (
        <div
          key={cfg.metric}
          data-testid="before-after-row"
          data-metric={cfg.metric}
          data-format={cfg.format}
          className={clsx(
            'grid items-center border-b border-panel-border/40 last:border-b-0',
            ROW_COLS,
          )}
        >
          <div className="px-3 py-1.5 text-[10.5px] font-mono text-text uppercase tracking-[0.3px]">
            {cfg.metric}
          </div>
          <div
            data-testid="before-after-in-place"
            className="px-3 py-1.5 text-right text-[11px] font-mono tabular-nums text-text-bright font-medium"
          >
            {formatCell(before, cfg.format)}
          </div>
          <div
            data-testid="before-after-stabilized"
            className="px-3 py-1.5 text-right text-[11px] font-mono tabular-nums text-text-bright font-medium"
          >
            {formatCell(after, cfg.format)}
          </div>
          <div
            data-testid="before-after-delta"
            data-tone={delta.tone}
            className={clsx(
              'px-3 py-1.5 text-right flex flex-col items-end justify-center leading-tight',
              DELTA_TONE_TOKEN[delta.tone],
            )}
            style={{ background: 'rgba(0, 200, 83, 0.024)' }}
          >
            <span className="text-[11px] font-mono tabular-nums font-semibold">
              {delta.abs}
            </span>
            {delta.pct && (
              <span className="text-[9px] font-mono tabular-nums opacity-70">
                {delta.pct}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
